import type { Tool } from '@ag-ui/client';
import {
  AgentResponseUpdate,
  AIContent,
  ChatMessage,
  ChatRole,
  IChatMessageProgress,
  IChatStreamingProgressMessage,
  TextContent,
} from '../../../../libs/models/conversations/ChatMessage';
import { FunctionResultData } from '../../../../libs/models/agui/AgUiEvent';
import { normalizeFunctionCall } from '../../../../libs/models/agui/FunctionCallContent';
import { IMCPTool, ToolState } from '../../../../libs/models/app/mcp/Tool';
import { AppDispatch } from '../../../app/store';

// ============================================================
// 1. 도구 변환 (IMCPTool → AG-UI Tool)
// ============================================================

/** enabled 도구만, inputSchema → parameters로 매핑 */
export function toAgUiTools(mcpTools: IMCPTool[] | undefined): Tool[] {
  if (!mcpTools) return [];
  return mcpTools
    .filter((t) => t.enabled)
    .map((t) => ({
      name: t.name,
      description: t.description ?? '',
      parameters: withRequired(t.inputSchema),
    }));
}

/** 스킬 도구 3개. 스킬 모드일 때 tools 에 같이 보낸다. */
export const skillTools: Tool[] = [
  {
    name: 'load_skill',
    description: 'Loads the full content of a specific skill',
    parameters: {
      type: 'object',
      properties: { skillName: { type: 'string', description: 'The skill name to load' } },
      required: ['skillName'],
    },
  },
  {
    name: 'read_skill_resource',
    description: 'Reads a resource associated with a skill, such as references, assets, or dynamic data.',
    parameters: {
      type: 'object',
      properties: {
        skillName: { type: 'string', description: 'The skill name' },
        resourceName: { type: 'string', description: 'The resource name to read' },
      },
      required: ['skillName', 'resourceName'],
    },
  },
  {
    name: 'run_skill_script',
    description: 'Runs a script associated with a skill.',
    parameters: {
      type: 'object',
      properties: {
        skillName: { type: 'string', description: 'The skill name' },
        scriptName: { type: 'string', description: 'The script name to run' },
        arguments: { type: 'object', description: 'Arguments to pass to the script' },
      },
      required: ['skillName', 'scriptName'],
    },
  },
  {
    // 스킬 스크립트 실행에 필요한 라이브러리 설치 등 임의 명령 실행.
    name: 'run_command',
    description: 'Runs an arbitrary shell command (e.g. install a library required by a skill script).',
    parameters: {
      type: 'object',
      properties: {
        command: { type: 'string', description: 'The command to run (e.g. "pip")' },
        args: { type: 'array', items: { type: 'string' }, description: 'Command arguments (e.g. ["install","python-pptx"])' },
      },
      required: ['command'],
    },
  },
];

/**
 * required가 비어 있으면 properties 키 전체를 required로 주입.
 * (required가 없으면 LLM이 인자를 선택으로 간주해 빼먹고 호출 → MCP 검증 실패)
 */
function withRequired(inputSchema: IMCPTool['inputSchema']): IMCPTool['inputSchema'] {
  if (!inputSchema) return inputSchema;
  if (inputSchema.required?.length) return inputSchema;
  return { ...inputSchema, required: Object.keys(inputSchema.properties ?? {}) };
}

// ============================================================
// 2. AgentResponseUpdate 생성 (Redux v2 슬라이스 호환)
// ============================================================

function buildUpdate(
  sessionId: string,
  messageId: string,
  contents: AIContent[],
): AgentResponseUpdate {
  return {
    additionalProperties: {
      sessionId: sessionId as unknown as object,
      id: messageId as unknown as object,
    },
    agentId: '',
    authorName: 'assistant',
    contents,
    createdAt: Date.now(),
    rawRepresentation: {},
    role: ChatRole.Assistant,
  };
}

/** 텍스트 청크 스트리밍용 */
export function createTextStreamUpdate(sessionId: string, messageId: string, text: string): AgentResponseUpdate {
  const content: TextContent = { text, additionalProperties: {} as Record<string, object> };
  return buildUpdate(sessionId, messageId, [content as AIContent]);
}

/** 도구 호출(functionCall) 카드용 */
export function createFunctionCallUpdate(
  sessionId: string,
  messageId: string,
  toolCallId: string,
  toolCallName: string,
  argsJson: string,
): AgentResponseUpdate {
  let parsedArgs: Record<string, object> = {};
  try {
    parsedArgs = argsJson ? (JSON.parse(argsJson) as Record<string, object>) : {};
  } catch {
    parsedArgs = {};
  }
  const content = {
    $type: 'functionCall',
    name: toolCallName,
    callId: toolCallId,
    arguments: parsedArgs,
    status: 'awaiting',
    additionalProperties: {} as Record<string, object>,
  } as unknown as AIContent;
  return buildUpdate(sessionId, messageId, [content]);
}

/** 도구 결과(functionResult) 카드용 */
export function createFunctionResultUpdate(
  sessionId: string,
  messageId: string,
  functionResult: FunctionResultData,
  callId?: string,
): AgentResponseUpdate {
  const content = {
    $type: 'functionResult',
    result: functionResult,
    callId,
    additionalProperties: {} as Record<string, object>,
  } as unknown as AIContent;
  return buildUpdate(sessionId, messageId, [content]);
}

// ============================================================
// 3. BE 커스텀 STATE_SNAPSHOT → Redux dispatch
// ============================================================

interface ChatMessageSnapshot {
  snapshotType: 'chatmessage';
  [key: string]: unknown;
}
interface AgentStateSnapshot {
  snapshotType: 'agentstate-update';
  chatId?: string;
  progress?: IChatMessageProgress;
}
type CustomSnapshot = ChatMessageSnapshot | AgentStateSnapshot;

/** chatmessage / agentstate-update 두 가지 처리 */
export function handleCustomSnapshot(dispatch: AppDispatch, sessionId: string, snapshot: CustomSnapshot) {
  if (snapshot.snapshotType === 'chatmessage') {
    const message = normalizeMessageContents(snapshot as unknown as ChatMessage);
    dispatch({ type: 'conversations/v2/addMessageToConversation', payload: { message } });
    return;
  }

  if (snapshot.snapshotType === 'agentstate-update') {
    const progress = snapshot.progress;
    if (!progress) return;
    dispatch({
      type: 'conversations/v2/updateBotResponseProgress',
      payload: { chatId: snapshot.chatId ?? sessionId, progress } as unknown as IChatStreamingProgressMessage,
    });
  }
}

function normalizeMessageContents(message: ChatMessage): ChatMessage {
  const raw = (message as unknown as { Contents?: unknown[] }).Contents;
  const sourceContents =
    message.contents && message.contents.length > 0
      ? message.contents
      : Array.isArray(raw)
        ? (raw as AIContent[])
        : undefined;
  if (!sourceContents) return message;

  const normalized = sourceContents.map((c) => normalizeFunctionCall(c) ?? c);
  return { ...message, contents: normalized };
}

// ============================================================
// 4. messageId 보정 (BE id가 Redux 슬롯과 안 맞을 때)
// ============================================================

interface MinimalConversationsState {
  conversations_v2: {
    conversations: Record<string, { messages?: { data?: ChatMessage[] } }>;
  };
}

/** 직접 매칭 → 못 찾거나 incomingMessageId가 비어있으면 마지막 어시스턴트 슬롯 id */
export function resolveAssistantSlotId(
  getState: () => unknown,
  sessionId: string,
  incomingMessageId: string | undefined,
): string | undefined {
  const state = getState() as MinimalConversationsState;
  const msgs = state.conversations_v2.conversations[sessionId]?.messages?.data ?? [];

  if (incomingMessageId) {
    const direct = msgs.find((m) => m.id === incomingMessageId || m.messageId === incomingMessageId);
    if (direct?.id) return direct.id;
  }

  for (let i = msgs.length - 1; i >= 0; i--) {
    if (msgs[i].role === ChatRole.Assistant && msgs[i].id) return msgs[i].id;
  }
  return incomingMessageId ? incomingMessageId : undefined;
}

/** 프론트가 관리하는 도구 만 노출 */
export function isFrontManagedTool(toolName: string | undefined, tools: ToolState): boolean {
  if (!toolName) return false;
  // 로컬 도구 
  if (skillTools.some((t) => t.name === toolName)) return true;
  // MCP 도구
  for (const list of Object.values(tools.localMcpTools ?? {})) {
    if (list?.some((t) => t.name === toolName)) return true;
  }
  // 커넥터 도구
  // for (const conn of tools.connectors?.data ?? []) {
  //   if (conn.tools?.some((t) => t.name === toolName)) return true;
  // }
  return false;
}
